import { Tooltip as MuiTooltip } from '@mui/material';
import { getComputedColor } from '@starsoft/common/utils';
import { TooltipComponentsProps } from './props';
import styles from './styles.module.scss';

/**
 * ToolTip Component
 * A customizable tooltip component that wraps MUI Tooltip with custom styling.
 * Features include cursor following, custom colors, and conditional rendering.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - The element that triggers the tooltip
 * @param {ReactNode} props.titleComponent - Content to show in the tooltip
 * @param {boolean} [props.visible=true] - Whether the tooltip should be visible
 * @param {boolean} props.invertColor - Whether to invert the background color scheme
 * @returns {JSX.Element} The rendered tooltip component or children if not visible
 *
 * @example
 * <ToolTip
 *   titleComponent={<span>Tooltip content</span>}
 *   visible={true}
 *   invertColor={false}
 * >
 *   <button>Hover me</button>
 * </ToolTip>
 */
export function ToolTip({
  children,
  titleComponent,
  visible = true,
  invertColor,
  placement = 'bottom',
}: TooltipComponentsProps) {
  return (
    (visible && (
      <MuiTooltip
        sx={{
          backgroundColor: getComputedColor(
            !invertColor ? '--card-color' : '--background-paper-color',
          ),
          color: getComputedColor('--text-primary-color'),
          padding: 'unset !important',
          maxWidth: 300,
          fontSize: '12px',
          fontFamily: 'Montserrat',
        }}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: getComputedColor(
                !invertColor ? '--card-color' : '--background-paper-color',
              ),
            },
          },
        }}
        placement={placement}
        title={<div className={styles.container}>{titleComponent}</div>}
      >
        {children}
      </MuiTooltip>
    )) ||
    children
  );
}
